import React, { FC, useEffect, useRef, useState, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../../../api';
import {
  AgentEvents,
  ConfirmationCreatedEvent,
  ConfirmationStatusChangedEvent,
  ThreadActivityUpdateEvent,
  ThreadPinnedEvent,
  ThreadUnpinnedEvent,
  ThreadArchivedEvent,
} from '@dreamai/types';
import { useAgent } from '../../../contexts/AgentContext';
import { useCompany } from '../../../contexts/CompanyContext';
import { usePusher } from '../../../contexts/PusherContext';
import SideNavSection, { SideNavSectionRef } from './SideNavSection';
import PinnedThreadsSection, {
  PinnedThreadsSectionRef,
} from './PinnedThreadsSection';
import TeamChatsSection, { TeamChatsSectionRef } from './TeamChatsSection';
import TasksSection, { TasksSectionRef } from './TasksSection';
import { useThreadReads } from '../../../providers/ThreadReadsProvider';
import { ReactComponent as SettingsIcon } from '../../../icons/nav-links/settings.svg';
import { ReactComponent as LogoutIcon } from '../../../icons/nav-links/logout.svg';
import { ReactComponent as DocumentsIcon } from '../../../icons/my-documents.svg';
import { ReactComponent as ClipboardIcon } from '../../../icons/clipboard-list.svg';
import { ReactComponent as MemoryIcon } from '../../../icons/nav-links/memory.svg';
import { ReactComponent as SalesInboxIcon } from '../../../icons/dreambox/email-inbox.svg';
import { ReactComponent as ArrowDownIcon } from '../../../icons/down-arrow.svg';
import { useSalesInboxCount } from '../SalesInbox/useSalesInboxCount';
import { useTasksCount } from './useTasksCount';
import './expansive-side-navigation.scss';

const ExpansiveSideNavigation: FC = () => {
  const { agents, loading, error, loadAgents } = useAgent();
  const { logout, getAccessTokenSilently, user } = useAuth0();
  const { memoryEnabled, isOpsInternalUser } = useCompany();
  const location = useLocation();
  const pusherContext = usePusher(true);
  const companyChannel = pusherContext?.pusherChannel;
  const userChannel = pusherContext?.userChannel;
  const { totalUnreadCount } = useThreadReads();
  const {
    needsAttentionCount: salesInboxUnreadCount,
    refresh: refreshSalesInbox,
  } = useSalesInboxCount();
  const { pendingCount: tasksCount, urgentCount: urgentTasksCount } =
    useTasksCount(isOpsInternalUser);

  const agentSectionsRef = useRef<HTMLDivElement>(null);
  const sectionRefs = useRef<Map<string, SideNavSectionRef>>(new Map());
  const pinnedSectionRef = useRef<PinnedThreadsSectionRef>(null);
  const teamChatsSectionRef = useRef<TeamChatsSectionRef>(null);
  const tasksSectionRef = useRef<TasksSectionRef>(null);

  const [hasUnreadBelow, setHasUnreadBelow] = useState(false);
  const [hasPendingBelow, setHasPendingBelow] = useState(false);
  const [hasMentionsBelow, setHasMentionsBelow] = useState(false);

  // Load agents on mount
  useEffect(() => {
    loadAgents();
  }, [loadAgents]);

  // Check if there are unread/pending items below the scroll viewport
  const checkItemsBelow = useCallback(() => {
    const container = agentSectionsRef.current;
    if (!container) return;

    const containerRect = container.getBoundingClientRect();
    const containerBottom = containerRect.bottom;

    let foundUnreadBelow = false;
    let foundPendingBelow = false;
    let foundMentionsBelow = false;

    // Check all thread items with unread, pending, or mentions classes
    const unreadItems = container.querySelectorAll('.thread-item.has-unread');
    const pendingItems = container.querySelectorAll(
      '.thread-item.has-pending-confirmations',
    );
    const mentionItems = container.querySelectorAll(
      '.thread-item.has-unread-mentions',
    );

    unreadItems.forEach((item) => {
      const rect = item.getBoundingClientRect();
      if (rect.top > containerBottom) {
        foundUnreadBelow = true;
      }
    });

    pendingItems.forEach((item) => {
      const rect = item.getBoundingClientRect();
      if (rect.top > containerBottom) {
        foundPendingBelow = true;
      }
    });

    mentionItems.forEach((item) => {
      const rect = item.getBoundingClientRect();
      if (rect.top > containerBottom) {
        foundMentionsBelow = true;
      }
    });

    setHasUnreadBelow(foundUnreadBelow);
    setHasPendingBelow(foundPendingBelow);
    setHasMentionsBelow(foundMentionsBelow);
  }, []);

  // Check on scroll, resize, and when unread count changes
  useEffect(() => {
    const container = agentSectionsRef.current;
    if (!container) return;

    const handleScroll = () => checkItemsBelow();
    const handleResize = () => checkItemsBelow();

    container.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);

    // Use MutationObserver to detect when thread items are added/removed
    const observer = new MutationObserver(() => {
      checkItemsBelow();
    });
    observer.observe(container, { childList: true, subtree: true });

    // Initial check
    checkItemsBelow();

    return () => {
      container.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
      observer.disconnect();
    };
  }, [checkItemsBelow, agents, totalUnreadCount]);

  // Listen for Pusher events to refresh threads
  useEffect(() => {
    if (!companyChannel) return;

    const handleConfirmationCreated = (data: ConfirmationCreatedEvent) => {
      // Refresh the section for this agent
      sectionRefs.current.get(data.agentId)?.refreshThreads();
      // Re-check items below after a short delay
      setTimeout(checkItemsBelow, 100);
    };

    const handleConfirmationStatusChanged = (
      data: ConfirmationStatusChangedEvent,
    ) => {
      sectionRefs.current.get(data.agentId)?.refreshThreads();
      setTimeout(checkItemsBelow, 100);
    };

    const handleThreadActivityUpdate = (data: ThreadActivityUpdateEvent) => {
      // Refresh all sections since we don't know which agent this thread belongs to
      sectionRefs.current.forEach((ref) => ref.refreshThreads());
      pinnedSectionRef.current?.refreshPinnedThreads();
      teamChatsSectionRef.current?.refreshTeamChats();
      tasksSectionRef.current?.refreshThreads();
      setTimeout(checkItemsBelow, 100);
    };

    const handleSalesInboxReplyReceived = () => {
      refreshSalesInbox();
    };

    const handleSalesInboxConversationViewed = (data: { userId: string }) => {
      // Only refresh if this user viewed the conversation (syncs across devices)
      refreshSalesInbox();
    };

    companyChannel.bind(
      AgentEvents.CONFIRMATION_CREATED,
      handleConfirmationCreated,
    );
    companyChannel.bind(
      AgentEvents.CONFIRMATION_STATUS_CHANGED,
      handleConfirmationStatusChanged,
    );
    companyChannel.bind(
      AgentEvents.THREAD_ACTIVITY_UPDATE,
      handleThreadActivityUpdate,
    );
    companyChannel.bind(
      AgentEvents.SALES_INBOX_REPLY_RECEIVED,
      handleSalesInboxReplyReceived,
    );
    companyChannel.bind(
      AgentEvents.SALES_INBOX_CONVERSATION_VIEWED,
      handleSalesInboxConversationViewed,
    );

    return () => {
      companyChannel.unbind(
        AgentEvents.CONFIRMATION_CREATED,
        handleConfirmationCreated,
      );
      companyChannel.unbind(
        AgentEvents.CONFIRMATION_STATUS_CHANGED,
        handleConfirmationStatusChanged,
      );
      companyChannel.unbind(
        AgentEvents.THREAD_ACTIVITY_UPDATE,
        handleThreadActivityUpdate,
      );
      companyChannel.unbind(
        AgentEvents.SALES_INBOX_REPLY_RECEIVED,
        handleSalesInboxReplyReceived,
      );
      companyChannel.unbind(
        AgentEvents.SALES_INBOX_CONVERSATION_VIEWED,
        handleSalesInboxConversationViewed,
      );
    };
  }, [companyChannel, checkItemsBelow, refreshSalesInbox, user?.sub]);

  // Listen for pin/unpin events on user channel for real-time sync
  useEffect(() => {
    if (!userChannel) return;

    const handleThreadPinned = (data: ThreadPinnedEvent) => {
      // Refresh pinned section and agent sections
      pinnedSectionRef.current?.refreshPinnedThreads();
      teamChatsSectionRef.current?.refreshTeamChats();
      if (data.agentId) {
        sectionRefs.current.get(data.agentId)?.refreshThreads();
      } else {
        // Refresh all sections if we don't know the agent
        sectionRefs.current.forEach((ref) => ref.refreshThreads());
      }
      setTimeout(checkItemsBelow, 100);
    };

    const handleThreadUnpinned = (data: ThreadUnpinnedEvent) => {
      // Refresh pinned section and all agent sections
      pinnedSectionRef.current?.refreshPinnedThreads();
      teamChatsSectionRef.current?.refreshTeamChats();
      sectionRefs.current.forEach((ref) => ref.refreshThreads());
      setTimeout(checkItemsBelow, 100);
    };

    const handleThreadArchived = (data: ThreadArchivedEvent) => {
      // Refresh all sections to remove the archived thread
      pinnedSectionRef.current?.refreshPinnedThreads();
      teamChatsSectionRef.current?.refreshTeamChats();
      if (data.agentId) {
        sectionRefs.current.get(data.agentId)?.refreshThreads();
      } else {
        sectionRefs.current.forEach((ref) => ref.refreshThreads());
      }
      setTimeout(checkItemsBelow, 100);
    };

    userChannel.bind(AgentEvents.THREAD_PINNED, handleThreadPinned);
    userChannel.bind(AgentEvents.THREAD_UNPINNED, handleThreadUnpinned);
    userChannel.bind(AgentEvents.THREAD_ARCHIVED, handleThreadArchived);

    return () => {
      userChannel.unbind(AgentEvents.THREAD_PINNED, handleThreadPinned);
      userChannel.unbind(AgentEvents.THREAD_UNPINNED, handleThreadUnpinned);
      userChannel.unbind(AgentEvents.THREAD_ARCHIVED, handleThreadArchived);
    };
  }, [userChannel, checkItemsBelow]);

  const handleLogout = () => {
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  const isActive = (path: string) => {
    return location.pathname.startsWith(path);
  };

  const scrollToFirstOffscreenItem = () => {
    const container = agentSectionsRef.current;
    if (!container) return;

    const containerRect = container.getBoundingClientRect();
    const containerBottom = containerRect.bottom;

    // Find all unread, pending, and mention items below viewport
    const unreadItems = Array.from(
      container.querySelectorAll('.thread-item.has-unread'),
    );
    const pendingItems = Array.from(
      container.querySelectorAll('.thread-item.has-pending-confirmations'),
    );
    const mentionItems = Array.from(
      container.querySelectorAll('.thread-item.has-unread-mentions'),
    );

    // Combine and find items below viewport
    const allItems = [...pendingItems, ...unreadItems, ...mentionItems];
    const offscreenItems = allItems.filter((item) => {
      const rect = item.getBoundingClientRect();
      return rect.top > containerBottom;
    });

    if (offscreenItems.length === 0) return;

    // Sort by position (top of element) to get the first one
    offscreenItems.sort((a, b) => {
      return a.getBoundingClientRect().top - b.getBoundingClientRect().top;
    });

    const firstItem = offscreenItems[0];
    const itemRect = firstItem.getBoundingClientRect();
    const scrollOffset = itemRect.top - containerRect.top - 20; // 20px padding from top

    container.scrollTo({
      top: container.scrollTop + scrollOffset,
      behavior: 'smooth',
    });
  };

  return (
    <nav className='expansive-side-navigation'>
      {/* Header with logo and actions */}
      <div className='header'>
        <div className='logo'>
          <Link to='/'>
            <img src='/dream-logo-black.png' alt='Dream' />
          </Link>
        </div>
        <div className='actions'>
          <Link to='/settings'>
            <SettingsIcon />
          </Link>
          <button onClick={handleLogout}>
            <LogoutIcon />
          </button>
        </div>
      </div>

      {/* Main navigation links for ops users */}
      {isOpsInternalUser && (
        <div className='main-links'>
          <Link
            to='/tasks'
            className={`nav-link ${isActive('/tasks') ? 'active' : ''}`}
          >
            <ClipboardIcon />
            <span>Tasks</span>
            {urgentTasksCount > 0 ? (
              <span className='tasks-badge urgent'>{urgentTasksCount}</span>
            ) : tasksCount > 0 ? (
              <span className='tasks-badge'>{tasksCount}</span>
            ) : null}
          </Link>
        </div>
      )}

      {/* Main navigation links - hidden for ops users */}
      {!isOpsInternalUser && (
        <div className='main-links'>
          <Link
            to='/sales-inbox'
            className={`nav-link ${isActive('/sales-inbox') ? 'active' : ''}`}
          >
            <SalesInboxIcon />
            <span>Sales Inbox</span>
            {salesInboxUnreadCount > 0 && (
              <span className='unread-badge'>{salesInboxUnreadCount}</span>
            )}
          </Link>
          <Link
            to='/docs'
            className={`nav-link ${isActive('/docs') ? 'active' : ''}`}
          >
            <DocumentsIcon />
            <span>Your Documents</span>
          </Link>
          {memoryEnabled && (
            <Link
              to='/memory'
              className={`nav-link ${isActive('/memory') ? 'active' : ''}`}
            >
              <MemoryIcon />
              <span>Memories</span>
            </Link>
          )}
        </div>
      )}

      {/* Agent sections */}
      <div className='agent-sections' ref={agentSectionsRef}>
        {/* Pinned threads at the top */}
        <PinnedThreadsSection ref={pinnedSectionRef} />

        {/* For ops users: show Tasks section with all threads */}
        {isOpsInternalUser ? (
          <TasksSection ref={tasksSectionRef} />
        ) : (
          <>
            {/* Team chats (shared with me + shared by me) */}
            <TeamChatsSection ref={teamChatsSectionRef} />

            {agents.map((agent) => (
              <SideNavSection
                key={agent.id}
                agentId={agent.id}
                ref={(ref) => {
                  if (ref) {
                    sectionRefs.current.set(agent.id, ref);
                  } else {
                    sectionRefs.current.delete(agent.id);
                  }
                }}
              />
            ))}
          </>
        )}
      </div>

      {/* Indicator for items below viewport */}
      {(hasUnreadBelow || hasPendingBelow || hasMentionsBelow) && (
        <button
          className={`items-below-indicator ${hasMentionsBelow ? 'has-mentions' : hasPendingBelow ? 'has-pending' : 'has-unread'}`}
          onClick={scrollToFirstOffscreenItem}
        >
          <ArrowDownIcon />
          <div>
            {hasMentionsBelow
              ? 'Unread mentions'
              : hasPendingBelow
                ? 'Pending actions'
                : 'Unread chats'}
          </div>
        </button>
      )}
    </nav>
  );
};

export default ExpansiveSideNavigation;
